import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  Bot,
  Users,
  Menu,
  X,
  Moon,
  Sun,
  User,
  Settings,
  LogOut,
  LayoutDashboard,
  Map,
  ScrollText,
} from 'lucide-react';
import { NotificationsBell } from 'features/community/components/NotificationsBell';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { toggleTheme } from 'app/providers/themeSlice';
import { toggleMobileNav, setMobileNavOpen, setSearchOpen, toggleSidebar } from 'app/providers/uiSlice';
import { logout } from 'features/auth/authSlice';
import { signOut, isSupabaseConfigured } from 'features/auth/api/authService';
import { ROUTES } from 'constants/routes';
import { learnerNav } from 'constants/copy';
import { Button } from 'components/ui/Button';
import { Avatar } from 'components/ui/Avatar';
import { BrandWordmark } from 'components/brand/BrandWordmark';
import { cn } from 'utils/cn';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { LearnerChromeControls } from 'components/navigation/LearnerChromeControls';
import { LearnerNavIdentity } from 'components/navigation/LearnerNavIdentity';
import { useDismissOnOutside } from 'components/navigation/useDismissOnOutside';
import { usePlatformTheme } from 'schools/parashari/hooks/usePlatformTheme';
import { useInParashariApp } from 'schools/parashari/providers/ParashariAppProviders';
import { useParashariLearnNavOptional } from 'schools/parashari/contexts/ParashariLearnNavContext';
import { SidebarCollapseButton } from 'components/navigation/SidebarCollapseButton';
import { HoverTooltip } from 'components/ui/HoverTooltip';
import { useSidebarPersist } from 'components/navigation/useSidebarPersist';
import { isLearnerNavLinkActive } from 'utils/learnerNavActive';
import { isMobileBottomNavDestination, type SidebarLink } from 'constants/learnerNavConfig';
import { useDialogFocus } from 'hooks/overlay/useDialogFocus';

type NavItemProps = {
  active?: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
};

function NavItem({ active, onClick, icon: Icon, label }: NavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('nav-pill hidden md:inline-flex', active && 'nav-pill-active')}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="hidden lg:inline">{label}</span>
    </button>
  );
}

export function TopNav({ onOpenEthics }: { onOpenEthics?: () => void } = {}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const learnNav = useParashariLearnNavOptional();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const user = useAppSelector((s) => s.auth.user);
  const resolved = useAppSelector((s) => s.theme.resolved);
  const mobileNavOpen = useAppSelector((s) => s.ui.mobileNavOpen);
  const { isDark } = usePlatformTheme();
  const inParashariApp = useInParashariApp();
  const [profileOpen, setProfileOpen] = useState(false);
  const fallbackMenuRef = useRef<HTMLDivElement>(null);

  const closeProfileMenu = useCallback(() => setProfileOpen(false), []);
  useDismissOnOutside(profileOpen && !inParashariApp, fallbackMenuRef, closeProfileMenu);

  useEffect(() => {
    if (!profileOpen || inParashariApp) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeProfileMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [profileOpen, inParashariApp, closeProfileMenu]);

  const homeHref = isAuthenticated ? ROUTES.DASHBOARD : ROUTES.HOME;

  const showSyllabus = Boolean(
    learnNav?.isOnLearnRoute &&
      (learnNav.currentPage === 'jyotisha' || learnNav.currentPage === 'jyotish-marga'),
  );
  const syllabusActive = learnNav?.currentPage === 'jyotish-marga';

  const handleSyllabusToggle = () => {
    if (!learnNav) return;
    if (learnNav.currentPage === 'jyotish-marga') {
      learnNav.setCurrentPage('jyotisha');
      window.dispatchEvent(new CustomEvent('jyotisha-reset-to-home'));
    } else {
      learnNav.setCurrentPage('jyotish-marga');
    }
  };

  const handleLogout = () => {
    setProfileOpen(false);
    dispatch(logout());
    navigate(ROUTES.HOME);
    if (isSupabaseConfigured) {
      void signOut();
    }
  };

  const openSearch = () => dispatch(setSearchOpen(true));

  return (
    <header className="app-header sticky top-0 z-[var(--z-header)] border-b glass-panel pt-safe-top shadow-subtle">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-4 lg:gap-6">
          <button
            className="nav-icon-btn -ml-2 md:hidden"
            onClick={() => dispatch(toggleMobileNav())}
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
          >
            {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Link
            to={homeHref}
            className="group shrink-0"
            aria-label="Takshify - The Digital Takshashila"
          >
            <BrandWordmark />
          </Link>
          {showSyllabus ? (
            <Button
              variant={syllabusActive ? 'primary' : 'ghost'}
              size="sm"
              onClick={handleSyllabusToggle}
              className="shrink-0"
              aria-pressed={syllabusActive}
            >
              <Map className="h-4 w-4" />
              <span>Syllabus</span>
            </Button>
          ) : null}
        </div>

        <div className="mx-8 hidden max-w-md flex-1 md:flex">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-tertiary" />
            <input
              type="search"
              placeholder="Search courses, lessons, schools..."
              className="input-premium h-10 w-full cursor-pointer pl-10 text-sm transition-colors placeholder:text-content-secondary hover:border-gold/30"
              aria-label="Search"
              onClick={openSearch}
              readOnly
            />
          </div>
        </div>

        <nav className="flex items-center gap-1 sm:gap-2">
          {isAuthenticated && (
            <>
              <NavItem
                onClick={() => navigate(ROUTES.AI_TUTOR)}
                icon={Bot}
                label={learnerNav.aiTutor}
              />
              <NavItem
                onClick={() => navigate(ROUTES.COMMUNITY)}
                icon={Users}
                label="Community"
              />
            </>
          )}
          {isAuthenticated && (
            <NotificationsBell />
          )}
          {isAuthenticated && inParashariApp ? (
            <LearnerChromeControls isDark={isDark} />
          ) : (
            <button
              type="button"
              onClick={() => dispatch(toggleTheme())}
              aria-label="Toggle theme"
              className="nav-icon-btn"
            >
              {resolved === 'dark' ? (
                <Sun className="h-4 w-4" aria-hidden />
              ) : (
                <Moon className="h-4 w-4" aria-hidden />
              )}
            </button>
          )}

          {isAuthenticated && inParashariApp ? (
            <LearnerNavIdentity
              profileOpen={profileOpen}
              onProfileOpenChange={setProfileOpen}
              onOpenEthics={onOpenEthics}
            />
          ) : isAuthenticated && user ? (
            <div ref={fallbackMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex min-h-[44px] min-w-[44px] items-center gap-2 rounded-lg p-1 transition-colors hover:bg-surface-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                aria-expanded={profileOpen}
                aria-haspopup="menu"
                aria-label="Open account menu"
              >
                <Avatar name={`${user.firstName} ${user.lastName}`} size="sm" />
              </button>
              {profileOpen ? (
                <div className="absolute right-0 top-full z-50 mt-2 w-56 animate-scale-in rounded-2xl border border-border bg-surface py-2 shadow-modal">
                    <div className="border-b border-border px-4 py-2">
                      <p className="text-sm font-medium">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="truncate text-caption">{user.email}</p>
                      {user.institutionNames?.length ? (
                        <p className="mt-0.5 truncate text-xs text-content-secondary">
                          {user.institutionNames.join(' · ')}
                        </p>
                      ) : null}
                    </div>
                    <Link
                      to={ROUTES.DASHBOARD}
                      className="flex min-h-[44px] items-center gap-2 px-4 py-2 text-sm hover:bg-surface-secondary"
                      onClick={() => setProfileOpen(false)}
                    >
                      <LayoutDashboard className="h-4 w-4" /> Dashboard
                    </Link>
                    <Link
                      to={ROUTES.PROFILE}
                      className="flex min-h-[44px] items-center gap-2 px-4 py-2 text-sm hover:bg-surface-secondary"
                      onClick={() => setProfileOpen(false)}
                    >
                      <User className="h-4 w-4" /> {learnerNav.progress}
                    </Link>
                    <Link
                      to={ROUTES.SETTINGS}
                      className="flex min-h-[44px] items-center gap-2 px-4 py-2 text-sm hover:bg-surface-secondary"
                      onClick={() => setProfileOpen(false)}
                    >
                      <Settings className="h-4 w-4" /> Settings
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-sm text-danger hover:bg-surface-secondary"
                    >
                      <LogOut className="h-4 w-4" /> Sign out
                    </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => navigate(ROUTES.LOGIN)}>
                Sign in
              </Button>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}

interface SidebarProps {
  links: SidebarLink[];
  title?: string;
}

/** True when this link opens a new group - drives the section heading / divider. */
function startsSection(links: SidebarLink[], index: number): boolean {
  const section = links[index]?.section;
  return Boolean(section) && section !== links[index - 1]?.section;
}

export function Sidebar({ links, title }: SidebarProps) {
  const dispatch = useAppDispatch();
  /** User-pinned open (persisted). Hover expands temporarily when collapsed. */
  const pinnedOpen = useSidebarPersist();
  const [hovered, setHovered] = useState(false);
  const leaveTimerRef = useRef<number | null>(null);
  const location = useLocation();
  const expanded = pinnedOpen || hovered;

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current != null) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  useEffect(() => () => clearLeaveTimer(), []);

  const handleMouseEnter = () => {
    clearLeaveTimer();
    if (!pinnedOpen) setHovered(true);
  };

  const handleMouseLeave = () => {
    clearLeaveTimer();
    // Short delay avoids flicker when moving between header / links
    leaveTimerRef.current = window.setTimeout(() => setHovered(false), 120);
  };

  const handleToggle = () => {
    clearLeaveTimer();
    setHovered(false);
    dispatch(toggleSidebar());
  };

  return (
    <aside
      className={cn(
        'relative z-[var(--z-nav,30)] hidden shrink-0 md:flex',
        pinnedOpen ? 'w-64' : 'w-[4.5rem]',
      )}
    >
      <div
        className={cn(
          'flex h-full min-h-full flex-col border-r border-border bg-surface-secondary/95 backdrop-blur-sm transition-[width,box-shadow] duration-300 ease-in-out motion-reduce:transition-none',
          expanded ? 'w-64' : 'w-[4.5rem]',
          !pinnedOpen && hovered && 'absolute inset-y-0 left-0 z-[var(--z-nav,30)] shadow-modal',
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={cn(
            'flex items-center border-b border-border',
            expanded ? 'gap-2 px-3 py-4' : 'flex-col justify-center gap-2 px-2 py-4',
          )}
        >
          {expanded ? (
            <>
              <h2 className="min-w-0 flex-1 truncate text-label text-accent-strong dark:text-gold-light">
                {title}
              </h2>
              <SidebarCollapseButton expanded={pinnedOpen} onToggle={handleToggle} />
            </>
          ) : (
            <>
              <HoverTooltip label={title ?? 'Navigation'} enabled>
                <div
                  className="btn-gold flex h-9 w-9 items-center justify-center rounded-xl shadow-gold"
                  aria-hidden
                >
                  <ScrollText className="h-4 w-4" />
                </div>
              </HoverTooltip>
              <HoverTooltip label="Pin navigation open" enabled className="w-auto shrink-0">
                <SidebarCollapseButton expanded={false} onToggle={handleToggle} />
              </HoverTooltip>
            </>
          )}
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden p-2" aria-label={title}>
          {links.map((link, index) => {
            const active = isLearnerNavLinkActive(link, {
              pathname: location.pathname,
              search: location.search,
              state: location.state,
            });
            const newSection = startsSection(links, index);
            return (
              <Fragment key={`${link.to}-${link.label}`}>
                {newSection && expanded ? (
                  <h3 className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary first:pt-0">
                    {link.section}
                  </h3>
                ) : null}
                {newSection && !expanded && index > 0 ? (
                  <hr className="mx-3 my-2 border-t border-border" />
                ) : null}
                <HoverTooltip label={link.label} enabled={!expanded}>
                  <Link
                    to={link.to}
                    state={link.navigateState}
                    aria-label={!expanded ? link.label : undefined}
                    className={cn(
                      'flex w-full items-center rounded-xl text-sm font-medium transition-all duration-250 ease-in-out',
                      expanded ? 'gap-3 px-3 py-2.5' : 'justify-center px-2 py-2.5',
                      active
                        ? 'nav-link-active'
                        : 'text-content-secondary hover:bg-surface hover:text-content',
                    )}
                  >
                    <link.icon
                      className={cn(
                        'h-5 w-5 shrink-0',
                        active && 'text-accent-strong dark:text-gold-light',
                      )}
                    />
                    {expanded ? <span className="truncate">{link.label}</span> : null}
                  </Link>
                </HoverTooltip>
              </Fragment>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}

export function MobileNav({ links }: { links: SidebarLink[] }) {
  const mobileNavOpen = useAppSelector((s) => s.ui.mobileNavOpen);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const drawerLinks = links.filter((link) => !isMobileBottomNavDestination(link.to));

  useDialogFocus(visible, panelRef);

  useEffect(() => {
    if (mobileNavOpen) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), 320);
    return () => window.clearTimeout(timer);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dispatch(setMobileNavOpen(false));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen, dispatch]);

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [mobileNavOpen]);

  if (!mounted) return null;

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-in-out md:hidden',
          visible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={() => dispatch(setMobileNavOpen(false))}
        aria-hidden
      />
      <nav
        ref={panelRef}
        className={cn(
          'fixed inset-y-0 left-0 z-[var(--z-drawer)] w-[min(18rem,calc(100vw-2.5rem))] border-r border-border bg-surface p-4 pt-safe-top shadow-modal transition-transform duration-350 ease-out md:hidden',
          visible ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-label="More navigation"
        tabIndex={-1}
      >
        <div className="mb-4 flex items-center justify-between gap-2 px-1">
          <p className="text-label">More</p>
          <button
            type="button"
            onClick={() => dispatch(setMobileNavOpen(false))}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-1 overflow-y-auto">
          {drawerLinks.map((link, index) => {
            const active = isLearnerNavLinkActive(
              link,
              {
                pathname: location.pathname,
                search: location.search,
                state: location.state,
              },
            );
            return (
              <Fragment key={`${link.to}-${link.label}`}>
                {startsSection(drawerLinks, index) ? (
                  <h3 className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-content-tertiary first:pt-0">
                    {link.section}
                  </h3>
                ) : null}
                <Link
                  to={link.to}
                  state={link.navigateState}
                  onClick={() => dispatch(setMobileNavOpen(false))}
                  className={cn(
                    'flex min-h-[44px] items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-250',
                    active
                      ? 'nav-link-active'
                      : 'text-content-secondary hover:bg-surface-secondary'
                  )}
                >
                  <link.icon className="h-5 w-5 shrink-0" aria-hidden />
                  {link.label}
                </Link>
              </Fragment>
            );
          })}
        </div>
      </nav>
    </>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-content-secondary">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span aria-hidden="true">/</span>}
          {item.to ? (
            <Link to={item.to} className="hover:text-content transition-colors">
              {item.label}
            </Link>
          ) : (
            <span className="text-content font-medium">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
